import { jwtDecode } from 'jwt-decode';

/*
 * Where sign-in returns to. A link from an email or a printed receipt (`/transactions/{id}`,
 * `/e/{code}`) opens the login page with `?next=<path>`, and a successful sign-in goes there.
 * Only a path on this site is followed: anything absolute, protocol-relative or on another
 * origin is dropped, so the parameter can't send a customer to someone else's page.
 */

export const NEXT_PARAM = 'next';

/** Where a sign-in goes when there is no return path. */
export const DEFAULT_AFTER_LOGIN = '/portal';

const LOGIN_PATH = '/login';

/** The same-origin path in `value`, or null when it is not one. */
export const safeNextPath = (
  value: string | null | undefined,
  origin: string = window.location.origin
): string | null => {
  if (!value || !value.startsWith('/')) return null;
  // `//host` and `/\host` are read by browsers as another host.
  if (value.startsWith('//') || value.startsWith('/\\')) return null;
  // Control characters (tabs, newlines) are stripped by URL parsing and can hide a scheme.
  if (Array.from(value).some((c) => c.charCodeAt(0) < 0x20 || c === '\x7f'))
    return null;
  let url: URL;
  try {
    url = new URL(value, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  // Returning to the login page itself would loop.
  if (url.pathname === LOGIN_PATH) return null;
  return `${url.pathname}${url.search}${url.hash}`;
};

/** The login page, carrying `next` when it is a path worth returning to. */
export const loginPath = (next?: string | null): string => {
  const path = safeNextPath(next);
  if (!path || path === '/') return LOGIN_PATH;
  return `${LOGIN_PATH}?${NEXT_PARAM}=${encodeURIComponent(path)}`;
};

/**
 * Pages anyone may open without an account: a printed receipt, a referral link, and the login
 * page itself. A session that fails on one of them is dropped, but the visitor stays put.
 */
const SESSION_OPTIONAL_PATHS: readonly RegExp[] = [
  /^\/e\/[^/]+\/?$/,
  /^\/r\/[^/]+\/?$/,
  /^\/referral\/?$/,
  /^\/login\/?$/
];

export const isSessionOptionalPath = (pathname: string): boolean =>
  SESSION_OPTIONAL_PATHS.some((pattern) => pattern.test(pathname));

/**
 * Where a lost session sends the customer: the login page, returning to the `next` this page
 * was itself carrying on the way somewhere, else to this page.
 */
export const loginPathFrom = (location: {
  pathname: string;
  search: string;
  hash: string;
}): string => {
  const carried = safeNextPath(
    new URLSearchParams(location.search).get(NEXT_PARAM)
  );
  return loginPath(
    carried ?? `${location.pathname}${location.search}${location.hash}`
  );
};

/** Whether this browser holds a sign-in that has not expired. */
export const hasLiveSession = (): boolean => {
  const token = localStorage.getItem('token');
  if (!token) return false;
  try {
    const { exp } = jwtDecode(token);
    return typeof exp === 'number' && exp * 1000 > Date.now();
  } catch {
    return false;
  }
};
